/**
 * /admin/create — unified entry point for every creation flow.
 *
 * Tile grid in two groups:
 *   Cricket entities  → deep-link into the existing /cricket/<entity>/add
 *                       forms in the main app (Player / Team / Stadium /
 *                       Umpire / Match / Tournament / Competition / Series).
 *   Marketplace       → in-admin creation (Pack, Scorer Verification,
 *                       Topup Pack — each routes to the right admin tab
 *                       with ?action=new so the page knows to open its
 *                       new-entity flow).
 *
 * The cricket tiles take the founder out of /admin/ into the main scoring
 * UI. That's intentional — those forms already exist and are battle-tested,
 * and the founder is comfortable jumping between the two contexts. The
 * "Back to admin" pattern in the AddX pages handles the return trip.
 */
import { Link } from "react-router-dom";
import {
  Plus, Users as UsersIcon, Shield, MapPin, UserCheck,
  Trophy, Calendar, Layers, Store, Package, Coins,
  Gavel, Building2,
} from "lucide-react";

const CRICKET_TILES = [
  {
    label: "Player",
    description: "Individual cricketer — name, nationality, role, hand.",
    to: "/cricket/players/add",
    icon: UsersIcon,
    color: "emerald",
  },
  {
    label: "Team",
    description: "Logical team — name, category, master squad.",
    to: "/cricket/teams/add",
    icon: Shield,
    color: "emerald",
  },
  {
    label: "Stadium",
    description: "Venue with pitch type + capacity.",
    to: "/cricket/stadiums/add",
    icon: MapPin,
    color: "emerald",
  },
  {
    label: "Umpire",
    description: "Match official roster entry.",
    to: "/cricket/umpires/add",
    icon: UserCheck,
    color: "emerald",
  },
  {
    label: "Match",
    description: "Fresh match — pick teams + venue, then score.",
    to: "/cricket/matches/add",
    icon: Calendar,
    color: "emerald",
  },
  {
    label: "Tournament",
    description: "Multi-team competition shell.",
    to: "/cricket/tournaments/add",
    icon: Trophy,
    color: "emerald",
  },
  {
    label: "Competition",
    description: "Season / series inside a tournament.",
    to: "/cricket/competitions/add",
    icon: Layers,
    color: "emerald",
  },
];

const MARKETPLACE_TILES = [
  {
    label: "Pack",
    description: "Marketplace bundle — players + teams or scored matches.",
    to: "/admin/marketplace?action=new",
    icon: Store,
    color: "indigo",
  },
  {
    label: "MATCH_BUNDLE pack",
    description: "Scored-match bundle — sell view-access to coded matches.",
    to: "/admin/marketplace?action=new&type=MATCH_BUNDLE",
    icon: Package,
    color: "indigo",
    badge: "NEW",
  },
  {
    label: "Squad/Fixture bundle",
    description: "Upload a CSV / XLSX of team rosters — players + teams auto-populate into the buyer's account.",
    to: "/admin/marketplace?action=new&type=SQUAD_PACK",
    icon: UsersIcon,
    color: "indigo",
    badge: "NEW",
  },
  {
    label: "Match Officials pack",
    description: "Upload a CSV / XLSX of umpires + referees — clones into the buyer's officials library on purchase.",
    to: "/admin/marketplace?action=new&type=OFFICIALS_PACK",
    icon: Gavel,
    color: "indigo",
    badge: "NEW",
  },
  {
    label: "Venues pack",
    description: "Upload a CSV / XLSX of stadiums — clones into the buyer's venues library on purchase.",
    to: "/admin/marketplace?action=new&type=VENUE_PACK",
    icon: Building2,
    color: "indigo",
    badge: "NEW",
  },
  {
    label: "Topup pack",
    description: "ZenCredit topup tile shown on customer Wallet pages.",
    to: "/admin/marketplace?action=new&kind=topup",
    icon: Coins,
    color: "indigo",
  },
];

function Tile({ tile }) {
  const Icon = tile.icon;
  const colorClasses = tile.color === "emerald"
    ? "border-emerald-500/30 hover:border-emerald-500/60 hover:bg-emerald-500/5"
    : "border-indigo-500/30 hover:border-indigo-500/60 hover:bg-indigo-500/5";
  const iconClass = tile.color === "emerald" ? "text-emerald-400" : "text-indigo-400";

  return (
    <Link
      to={tile.to}
      className={`group rounded-lg border ${colorClasses} bg-bg-primary p-4 transition-colors flex flex-col gap-2 relative`}
    >
      {tile.badge && (
        <span className="absolute top-3 right-3 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/40">
          {tile.badge}
        </span>
      )}
      <Icon className={`w-5 h-5 ${iconClass}`} />
      <div className="font-semibold text-sm">{tile.label}</div>
      <div className="text-xs text-text-secondary leading-snug">{tile.description}</div>
    </Link>
  );
}

export default function AdminCreate() {
  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Plus className="w-6 h-6 text-emerald-400" />
          Create
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          One entry point for every creatable entity. Cricket tiles open
          the standard Add form in the main app; marketplace tiles stay
          in admin.
        </p>
      </header>

      <section>
        <h2 className="text-xs uppercase tracking-widest text-text-secondary mb-3">
          Cricket entities
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {CRICKET_TILES.map((t) => <Tile key={t.label} tile={t} />)}
        </div>
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-widest text-text-secondary mb-3">
          Marketplace
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {MARKETPLACE_TILES.map((t) => <Tile key={t.label} tile={t} />)}
        </div>
      </section>
    </div>
  );
}
